
import type { Theme } from '@mui/material/styles';
import { black } from '../colors';

/**
 * Returns a responsive maxHeight object for DataGrid `.MuiDataGrid-main`.
 * @param mdOffset – the px offset used in `calc(100vh - Xpx)` for md+ screens.
 */
export const responsiveGridMaxHeight = (mdOffset: number) => ({
    xs: `calc(100vh - ${mdOffset - 100}px)`,
    sm: `calc(100vh - ${mdOffset - 40}px)`,
    md: `calc(100vh - ${mdOffset}px)`,
});

export const MtComStyleObj = (theme: Theme) => ({
    tabStyle: {
        textTransform: 'none', fontWeight: 600,
        fontSize: 16, minHeight: 0, height: '100%', paddingRight: 0,
        padding: '8px 16px',
    },
    tabSelected: {
        bgcolor: '#14803C',
        color: theme.palette.common.white,
        borderRadius: '6px',
        boxShadow: '0px 2px 4px 1px #00000014',
        padding: '8px 12px',
        height: '40px',
        gap: '8px',
        border: 'none',
        outline: 'none',
    },
    textInter16Bold: {
        fontWeight: 700, fontSize: 16, fontStyle: "bold"
    },
    landingPageArrow: {
        opacity: 0, color: theme.palette.common.white, fontSize: 32, display: 'flex', bottom: 0
    },
    dialogCloseButton: {
        top: 8,
        color: black,
        width: 24,
        height: 24
    },
    dialogBoxStyle: {
        borderRadius: "16px",
        '& .MuiInputBase-root': { height: 40 },
        '& .MuiDialogActions-root': {
            padding: "0px 24px"
        },
        '& .MuiDialog-paper': {
            [theme.breakpoints.up('sm')]: {
                width: 600,
                maxWidth: 'none',
            },
            [theme.breakpoints.up('md')]: {
                width: "50%",
                maxWidth: 'none',
                padding: "40px 32px"

            },
            [theme.breakpoints.up('lg')]: {
                width: "50%",
                maxWidth: 'none',
                padding: "40px 32px"

            },
        }
    },
    textNoto16Bold: {
        fontWeight: "bold", fontSize: 16
    },
    labelNoto14500: {
        fontWeight: 500, fontSize: 14
    },
    labelNoto30700: {
        fontWeight: 700, fontSize: 30
    },
    labelNoto16400: {
        fontWeight: 400, fontSize: 16
    },
    dividerStyle: {
        borderColor: "grey.400",
        borderBottomWidth: "1px",
        position: 'relative',
        bgcolor: 'background.paper',
        borderRadius: 1,
        width: "100%",
        marginTop: 5
    },
    drllDownCommentDialog: {

        '& .MuiDialogActions-root': {
            padding: "0px 24px"
        },
        '& .MuiDialog-paper': {
            [theme.breakpoints.up('sm')]: {
                width: 600,
                maxWidth: 'none',
            },
            [theme.breakpoints.up('md')]: {
                width: "50%",
                maxWidth: 'none',
                p: 0

            },
            [theme.breakpoints.up('lg')]: {
                width: "50%",
                maxWidth: 'none',
                p: 0
            },
        }
    },
    saveCommentStepsStyle: {
        bgcolor: '#AFB1B4',
        width: 38,
        height: 38,
        minWidth: 38,
        p: 0,
        borderRadius: 1,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        '& .MuiButton-startIcon, & .MuiButton-endIcon': {
            display: 'inherit',
            alignItems: 'center',
            m: 0,
        },

    },
    fieldFocusHoverStyle: {

        '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#00AC4F',
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#00AC4F',
        },

        '& .MuiInputLabel-root.Mui-focused': {
            color: '#00AC4F'
        }
    },
    textFocusHoverStyle: {

        '& .MuiOutlinedInput-root': {

            '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#00AC4F'
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#00AC4F'

            },
        }

    },

    gridIconHoverStyle: {
        p: 0,
        borderRadius: 0,
        '&:hover': { backgroundColor: 'transparent' },
        '&:active': { backgroundColor: 'transparent' },
        '& .MuiTouchRipple-root': { display: 'none' },
        '&:focus': { outline: 'none' },
        '&.Mui-focusVisible': { outline: 'none' },
        '&:focus-visible': { outline: 'none' },
    },

    actionColumnStyle: {
        '& .MuiDataGrid-actionsCell': {
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
        },
    },

    textBoxPlaceHolderStyle: {
        '& .MuiInputBase-input::placeholder': {
            color: '#808080',
            opacity: 1,
            fontSize: 14
        },
    },
    disabledGreenBtnStyle: {
        '&.Mui-disabled': {
            color: '#FFFFFF',
            backgroundColor: '#BEBEBE',
            textTransform: "none",
            height: "40px",
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
        },
        '&.Mui-disabled .MuiButton-startIcon, &.Mui-disabled .MuiButton-endIcon': {
            display: 'inherit',
            alignItems: 'center',
        },
    },
    disabledWhiteBtnStyle: {
        '&.Mui-disabled': {
            color: '#BEBEBE',
            borderColor: "#BEBEBE",
            backgroundColor: 'white',
            height: "40px",
            "border": "1px solid var(--Neutral-100, #BEBEBE)",
            textTransform: "none",
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
        },
        '&.Mui-disabled .MuiButton-startIcon, &.Mui-disabled .MuiButton-endIcon': {
            display: 'inherit',
            alignItems: 'center',
        },
    },
    disabledRedBtnStyle: {
        '&.Mui-disabled': {
            color: '#BEBEBE',
            borderColor: "#BEBEBE",
            backgroundColor: 'white',
            "border": "1px solid var(--Neutral-100, #BEBEBE)",
            textTransform: "none",
            height: "40px",
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
        },
        '&.Mui-disabled .MuiButton-startIcon, &.Mui-disabled .MuiButton-endIcon': {
            display: 'inherit',
            alignItems: 'center',
        },
    },
    textBoxValueStyle: {
        '& .MuiInputBase-input': {
            fontSize: 16, color: '#0A0A0A'
        },
        '& .MuiInputBase-input::placeholder': {
            fontSize: 14,
        },
    },
    dataGridHideSortButton: {
        backgroundColor: '#FFFFFF',
        '& .MuiDataGrid-sortButton': {
            display: 'none',
        },
    },
    gridHeaderText: {
        fontSize: 16,
        color: "#0A0A0A",
        fontWeight: 600
    },
    filterAccordianStyle: {
        width: "100%",
        border: "none", outline: "none",
        boxShadow: "none",
        '&:before': {
            display: "none",
        },

        '& .MuiAccordionSummary-root': {
            '&:focus': { outline: 'none' },
            '&:focus-visible': { outline: 'none' },
            '&.Mui-focusVisible': {
                outline: 'none',
                backgroundColor: 'transparent',
            },

        }
    },
    gridPaginationStyle: {
        "& .MuiTablePagination-actions .MuiIconButton-root": {
            '&:hover': {
                backgroundColor: "transparent",
                outline: 'none'
            },
            '&:focus': {
                backgroundColor: "transparent",
                outline: 'none'
            },
            '&:active': { backgroundColor: 'transparent' },
            '& .MuiTouchRipple-root': { display: 'none' },
        },
    },
    dataGridCellFocusStyle: {
        '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-visible': {
            outline: 'none',
        },
        '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-visible': {
            outline: 'none',
        },
    },
    placeHolderStyle: {
        '& .MuiInputBase-input::placeholder': {
            color: '#808080',
            opacity: 1,
            fontSize: 14,

        },
    },

    // --- DashboardHeader styles ---

    dashboardHeaderWrapper: {
        width: '100%',
    },
    dashboardHeaderRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: { xs: 'flex-start', sm: 'flex-start' },
        flexDirection: { xs: 'column', sm: 'row' },
        width: '100%',
        mb: 1,
        mt: 2,
        gap: { xs: 1, sm: 0 },
    },
    dashboardHeaderLeftCol: {
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        width: { xs: '100%', sm: 'auto' },
    },
    dashboardTitleRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        flexWrap: 'wrap',
    },
    dashboardTitle: {
        fontSize: { xs: '20px', sm: '24px', md: '28px' },
    },
    dashboardFilterChip: {
        width: 'auto',
        minWidth: 'unset',
        px: 2,
        fontSize: { xs: 12, sm: 14 },
    },
    dashboardActionButtons: {
        flexDirection: 'row',
        gap: 1,
        alignItems: 'center',
        flexWrap: 'wrap',
        width: { xs: '100%', sm: 'auto' },
        justifyContent: { xs: 'flex-end', sm: 'flex-start' },
    },
    dashboardFilterRow: {
        width: '100%',
        display: 'flex',
        alignItems: { xs: 'flex-start', sm: 'center' },
        flexDirection: { xs: 'column', sm: 'row' },
        mt: 2,
        gap: { xs: 1, sm: 0 },
    },
    dashboardChipsWrapper: {
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        overflow: 'auto',
        maxWidth: '100%',
    },
    dashboardSearchRow: {
        display: 'flex',
        gap: 1,
        alignItems: 'center',
        alignSelf: { xs: 'flex-end', sm: 'center' },
    },
});

/**
 * Common grid cell and header styles used across all DataGrids.
 * Includes focus styles, padding, and basic layout.
 */
export const getGridCommonCellStyles = () => ({
    '& .MuiDataGrid-cell': {
        padding: '10px 10px',
        display: 'flex',
        alignItems: 'center',
    },
    '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-visible': {
        outline: 'none',
    },
    '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-visible': {
        outline: 'none',
    },
});

/**
 * Generates DataGrid styles with customizable header and scrollbar colors.
 * @param theme – MUI Theme object
 * @param headerColor – Background color for column headers (default: '#DEDEDE')
 * @param fillerColor – Color for grid filler and scrollbar (default: '#DEDEDE')
 * @param maxHeight – Max height for grid container (default: 'calc(100vh - 310px)')
 * @param minHeight – Min height for grid container (default: 'auto')
 * @returns Comprehensive grid styling object
 */
export const getDataGridStyles = (
    theme: Theme,
    options?: {
        headerColor?: string;
        fillerColor?: string;
        maxHeight?: string;
        minHeight?: string;
    }
) => {
    const {
        headerColor = '#DEDEDE',
        fillerColor = '#DEDEDE',
        maxHeight = 'calc(100vh - 310px)',
        minHeight = 'auto',
    } = options || {};

    return {
        '& .MuiDataGrid-columnHeader': {
            backgroundColor: `${headerColor} !important`,
        },
        '& .MuiDataGrid-columnHeaderTitleContainerContent .MuiTypography-subtitle1': {
            ...MtComStyleObj(theme).gridHeaderText,
        },
        '& .MuiDataGrid-columnHeaderTitle': {
            ...MtComStyleObj(theme).gridHeaderText,
        },
        ...getGridCommonCellStyles(),
        ...MtComStyleObj(theme).dataGridHideSortButton,
        ...MtComStyleObj(theme).gridPaginationStyle,
        ...MtComStyleObj(theme).actionColumnStyle,
        '& .MuiDataGrid-filler, & .MuiDataGrid-scrollbarFiller': {
            borderLeft: '1px solid',
            borderColor: fillerColor,
            backgroundColor: fillerColor,
        },
        '& .MuiDataGrid-main': {
            minHeight,
            maxHeight,
            overflow: 'auto',
        },
    };
};

export const printLabelDialogStyles = {
    paper: { borderRadius: '16px', width: { xs: '95vw', sm: '90vw' }, maxWidth: 820, p: 0, overflow: 'hidden' },
    titleText: { fontWeight: 700, fontSize: { xs: '1rem', sm: '1.1rem' }, mb: 1.5 },
    contentLayout: { display: 'flex', flexWrap: 'wrap', minHeight: { xs: 'auto', sm: 280 }, width: '100%' },
    leftPanel: { flex: { xs: '1 1 100%', sm: '1 1 320px' }, p: { xs: 2, sm: 3 }, display: 'flex', flexDirection: 'column', minWidth: 0, boxSizing: 'border-box' },
    rightPanel: { flex: { xs: '1 1 100%', sm: '1 1 260px' }, p: { xs: 2, sm: 3 }, borderLeft: { xs: 'none', sm: '1px solid #e0e0e0' }, borderTop: { xs: '1px solid #e0e0e0', sm: 'none' }, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start', minWidth: 0, boxSizing: 'border-box' },
    splotIdText: { fontWeight: 400, fontSize: { xs: 16, sm: 18 }, lineHeight: '100%', mb: 2, color: '#4A5565' },
    infoFieldsBox: {
        display: 'flex', gap: 2, mb: 2, p: 1.5,
        border: '1px solid #e0e0e0', borderRadius: '8px', backgroundColor: '#FAFAFA',
        flexWrap: 'wrap',
    },
    infoFieldItem: { display: 'flex', flexDirection: 'column', minWidth: 120 },
    infoFieldLabel: { fontWeight: 400, fontSize: '0.75rem', color: '#888', lineHeight: 1.4 },
    infoFieldValue: { fontWeight: 500, fontSize: '0.85rem', color: '#333', lineHeight: 1.4 },
    templateBox: { backgroundColor: '#f0f7f0', border: '1px solid #c8e6c9', borderRadius: '12px', p: 2, mb: 2, width: '100%', boxSizing: 'border-box' },
    templateTitle: { fontWeight: 600, fontSize: { xs: 14, sm: 16 }, lineHeight: '16px', letterSpacing: 0, wordBreak: 'break-word' },
    templateSubtitle: { color: '#2e7d32', fontSize: '0.85rem', mt: 0.5 },
    changeLink: { color: '#155DFC', fontWeight: 500, fontSize: 14, lineHeight: '19px', textDecoration: 'underline', textDecorationStyle: 'solid', cursor: 'pointer', alignSelf: 'flex-start', '&:hover': { color: '#073a9c' } },
    labelCaption: { color: '#666', mb: 1.5, fontSize: '0.85rem' },
    actions: { px: { xs: 2, sm: 3 }, py: 2, gap: 1.5, flexWrap: 'wrap', justifyContent: { xs: 'center', sm: 'flex-end' } },
    cancelBtn: {
        textTransform: 'none', fontWeight: 600, width: 75, height: 40, gap: '8px', borderRadius: '4px',
        padding: '10px 12px', border: '1px solid #000', fontSize: '0.9rem', color: '#333',
        '&:hover': { backgroundColor: 'white', boxShadow: 'none', outline: 'none', border: '1px solid #000' },
    },
    printBtn: {
        textTransform: 'none', fontWeight: 600, py: 1, px: 3, fontSize: '0.9rem', borderRadius: '4px', boxShadow: 'none',
    },
    labelContainer: {
        border: '1px solid #e0e0e0',
        borderRadius: '12px',
        display: 'grid',
        gridTemplateRows: '1fr 1fr',
        overflow: 'hidden',
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
    },
    labelRow: { display: 'flex', alignItems: 'center', overflow: 'hidden', minHeight: 0, px: 0.5, gap: 1, width: '100%', boxSizing: 'border-box' },
    qrCell: { flexShrink: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', overflow: 'hidden', '& svg': { width: '100%', height: '100%' } },
    textCell: { display: 'flex', flexDirection: 'column', justifyContent: 'center', overflow: 'hidden', minWidth: 0, flex: 1 },
    textCellFlex: { display: 'flex', flexDirection: 'column', justifyContent: 'center', overflow: 'hidden', minWidth: 0, flex: 1 },
    splotIdLabel: { fontWeight: 700, fontSize: { xs: '0.75rem', sm: '0.85rem' }, letterSpacing: 0.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
    originLabel: { fontWeight: 700, fontSize: { xs: '0.6rem', sm: '0.7rem' }, lineHeight: 1.3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
    footerLabel: { fontWeight: 600, fontSize: { xs: '0.55rem', sm: '0.6rem' }, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
    adminCodeLabel: { fontWeight: 700, fontSize: { xs: '0.8rem', sm: '0.95rem' }, lineHeight: 1.3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
    footerRow: { display: 'flex', gap: 1 },
    printingOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        bgcolor: 'rgba(255, 255, 255, 0.7)',
        zIndex: 10,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        borderRadius: 'inherit',
    },
    // Vertical layout styles
    verticalWrapper: {
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', width: '100%', height: '100%',
        py: 0.5, px: 0.5, gap: '4px', boxSizing: 'border-box',
    },
    verticalQrCell: { flexShrink: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' },
    verticalTextGroup: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0, mt: 0 },
    verticalTextLabel: { textAlign: 'center', whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.2 },
} as const;
